import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Bell, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const safeDistance = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const dropRef = useRef(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const toggle = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ left: Math.min(r.right + 12, window.innerWidth - 336), top: Math.max(12, Math.min(r.top, window.innerHeight - 460)) });
    setOpen((v) => !v);
  };

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setItems(data || []);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchNotifications();
    const i = setInterval(fetchNotifications, 30000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !dropRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try {
      await axios.post(`${API_URL}/api/notifications/mark-all-read`, {}, { withCredentials: true });
      setItems((arr) => arr.map((n) => ({ ...n, read: true })));
    } catch {
      // silent
    }
  };

  const markOneRead = async (id) => {
    try {
      await axios.patch(`${API_URL}/api/notifications/${id}/read`, {}, { withCredentials: true });
      setItems((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
    } catch {
      // silent
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={toggle}
        className="relative p-2 rounded-full hover:bg-white/10 transition-colors"
        data-testid="notification-bell-button"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.75} className="text-zinc-300" />
        {unread > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF451A] text-[#0F0F0F] dark:text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(255,69,26,0.6)]"
            data-testid="notification-unread-count"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={dropRef}
          style={{ left: pos.left, top: pos.top }}
          className="fixed w-80 bg-white dark:bg-[#0F0F0F]/95 backdrop-blur-2xl border border-black/[0.10] dark:border-white/10 rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18),0_0_40px_rgba(255,69,26,0.08)] overflow-hidden z-[1000]"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between p-3 border-b border-black/[0.08] dark:border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent">
            <h4 className="font-semibold text-sm text-[#0F0F0F] dark:text-white tracking-tight">Notifications</h4>
            {unread > 0 && (
              <button
                onClick={markAllRead}
                className="text-xs text-[#FF451A] hover:text-[#C2410C] dark:hover:text-white font-medium flex items-center gap-1 transition-colors"
                data-testid="mark-all-read-button"
              >
                <Check size={11} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-[#6B6B6B] dark:text-zinc-500">No notifications yet</div>
            ) : (
              items.map((n) => {
                const inner = (
                  <div
                    onClick={() => !n.read && markOneRead(n.id)}
                    className={`p-3 border-b border-black/[0.08] dark:border-white/5 last:border-b-0 cursor-pointer hover:bg-black/[0.03] dark:hover:bg-white/[0.03] transition-colors ${
                      !n.read ? 'bg-[#FF451A]/[0.06]' : ''
                    }`}
                    data-testid="notification-item"
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#FF451A] shadow-[0_0_8px_#FF451A] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!n.read ? 'text-[#0F0F0F] dark:text-white font-medium' : 'text-[#6B6B6B] dark:text-zinc-400'}`}>{n.message}</p>
                        <p className="text-xs text-[#A1A1AA] dark:text-zinc-600 mt-1 font-mono uppercase tracking-wider">{safeDistance(n.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
                return n.link ? (
                  <Link key={n.id} to={n.link} onClick={() => setOpen(false)}>{inner}</Link>
                ) : (
                  <div key={n.id}>{inner}</div>
                );
              })
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
